import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Coins,
  FileText,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Repeat,
  Save,
  Sparkles,
  Trash2,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";
import { Expense } from "../../types/expenses";

type RecurrenceFrequency = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
type RecurrenceStatus = "ACTIVE" | "PAUSED" | "ENDED" | string;

type ExpenseWithRecurring = Expense & {
  is_recurring?: boolean;
  recurrence_frequency?: RecurrenceFrequency | string | null;
  recurrence_interval?: number | null;
  recurrence_start_date?: string | null;
  recurrence_end_date?: string | null;
  next_occurrence_date?: string | null;
  last_generated_date?: string | null;
  recurrence_status?: RecurrenceStatus | null;
  recurring_parent_id?: string | null;
  created_by_name?: string | null;
};

type FormState = {
  amount: string;
  currency: string;
  category: string;
  description: string;
  expense_date: string;
  is_recurring: boolean;
  recurrence_frequency: RecurrenceFrequency;
  recurrence_interval: string;
  recurrence_start_date: string;
  recurrence_end_date: string;
};

const CURRENCIES = ["KES", "USD", "TZS", "UGX"];

const CATEGORY_SUGGESTIONS = [
  "Bandwidth",
  "Equipment",
  "Rent",
  "Salaries",
  "Utilities",
  "Transport",
  "Maintenance",
  "Subscriptions",
];

const PAGE_SIZE = 10;

const formatAmount = (amount: number | string | undefined | null) => {
  return Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getTodayIso = () => new Date().toISOString().slice(0, 10);

const ExpensesPage: React.FC = () => {
  const { showToast } = useToast();

  const [expenses, setExpenses] = useState<ExpenseWithRecurring[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [generatingDue, setGeneratingDue] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [page, setPage] = useState(1);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pendingDelete, setPendingDelete] =
    useState<ExpenseWithRecurring | null>(null);

  const buildEmptyForm = (): FormState => {
    const todayIso = getTodayIso();

    return {
      amount: "",
      currency: "KES",
      category: "",
      description: "",
      expense_date: todayIso,
      is_recurring: false,
      recurrence_frequency: "MONTHLY",
      recurrence_interval: "1",
      recurrence_start_date: todayIso,
      recurrence_end_date: "",
    };
  };

  const [form, setForm] = useState<FormState>(buildEmptyForm);

  const resetMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const showErrorMsg = (msg: string) => {
    setError(msg);
    setSuccess(null);
  };

  const showSuccessMsg = (msg: string) => {
    setSuccess(msg);
    setError(null);
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      resetMessages();

      const data = await fetchApp<ExpenseWithRecurring[]>("/expenses/");
      setExpenses(Array.isArray(data) ? data : []);
      setPage(1);
    } catch (err: any) {
      const msg = err?.message || "Failed to load expenses";
      showErrorMsg(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const totalCount = expenses.length;
    const recurringCount = expenses.filter((e) => e.is_recurring).length;
    const generatedCount = expenses.filter((e) => e.recurring_parent_id).length;

    const countableExpenses = expenses.filter((e) => !e.is_recurring);

    const byCurrency: Record<string, number> = {};
    for (const e of countableExpenses) {
      const cur = e.currency || "KES";
      byCurrency[cur] = (byCurrency[cur] || 0) + Number(e.amount || 0);
    }

    const currencyEntries = Object.entries(byCurrency);
    const totalLabel =
      currencyEntries.length === 0
        ? "0.00 KES"
        : currencyEntries.length === 1
          ? `${formatAmount(currencyEntries[0][1])} ${currencyEntries[0][0]}`
          : `${currencyEntries.length} currencies`;

    return {
      totalCount,
      recurringCount,
      generatedCount,
      totalLabel,
    };
  }, [expenses]);

  const totalPages = Math.max(1, Math.ceil(expenses.length / PAGE_SIZE));

  const paginatedExpenses = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return expenses.slice(start, start + PAGE_SIZE);
  }, [expenses, page]);

  const startEntry = expenses.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endEntry = Math.min(page * PAGE_SIZE, expenses.length);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetMessages();

    if (!form.amount.trim()) {
      const msg = "Amount is required";
      showErrorMsg(msg);
      showToast(msg, "error");
      return;
    }

    if (!form.expense_date) {
      const msg = "Expense date is required";
      showErrorMsg(msg);
      showToast(msg, "error");
      return;
    }

    const amountNum = Number(form.amount);
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      const msg = "Amount must be a positive number";
      showErrorMsg(msg);
      showToast(msg, "error");
      return;
    }

    const intervalNum = Number(form.recurrence_interval || 1);
    if (form.is_recurring && (Number.isNaN(intervalNum) || intervalNum < 1)) {
      const msg = "Recurring interval must be at least 1";
      showErrorMsg(msg);
      showToast(msg, "error");
      return;
    }

    if (form.is_recurring && !form.recurrence_frequency) {
      const msg = "Recurring frequency is required";
      showErrorMsg(msg);
      showToast(msg, "error");
      return;
    }

    if (
      form.is_recurring &&
      form.recurrence_end_date &&
      form.recurrence_end_date <
        (form.recurrence_start_date || form.expense_date)
    ) {
      const msg = "End date cannot be before start date";
      showErrorMsg(msg);
      showToast(msg, "error");
      return;
    }

    try {
      setSaving(true);

      const body = {
        amount: amountNum,
        currency: form.currency || "KES",
        category: form.category || undefined,
        description: form.description || undefined,
        expense_date: form.expense_date,
        is_recurring: form.is_recurring,
        recurrence_frequency: form.is_recurring
          ? form.recurrence_frequency
          : undefined,
        recurrence_interval: form.is_recurring ? intervalNum : undefined,
        recurrence_start_date: form.is_recurring
          ? form.recurrence_start_date || form.expense_date
          : undefined,
        recurrence_end_date: form.is_recurring
          ? form.recurrence_end_date || undefined
          : undefined,
      };

      const res = await fetchApp<ExpenseWithRecurring>("/expenses/", {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!res || !res.id) {
        throw new Error("Failed to create expense");
      }

      const successMsg = form.is_recurring
        ? "Recurring expense created successfully"
        : "Expense recorded successfully";

      showSuccessMsg(successMsg);
      showToast(successMsg, "success");

      setForm(buildEmptyForm());
      await fetchExpenses();
    } catch (err: any) {
      const msg = err?.message || "Failed to save expense";
      showErrorMsg(msg);
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateDueExpenses = async () => {
    try {
      setGeneratingDue(true);
      resetMessages();

      const res = await fetchApp<{ generated_count: number }>(
        "/expenses/recurring/generate-due",
        {
          method: "POST",
        },
      );

      const count = Number(res?.generated_count || 0);
      const msg =
        count === 1
          ? "1 due recurring expense generated"
          : `${count} due recurring expenses generated`;

      showSuccessMsg(msg);
      showToast(msg, "success");
      await fetchExpenses();
    } catch (err: any) {
      const msg = err?.message || "Failed to generate due recurring expenses";
      showErrorMsg(msg);
      showToast(msg, "error");
    } finally {
      setGeneratingDue(false);
    }
  };

  const handlePauseResumeRecurring = async (exp: ExpenseWithRecurring) => {
    if (!exp.is_recurring) return;

    const isPaused = exp.recurrence_status === "PAUSED";
    const action = isPaused ? "resume" : "pause";

    try {
      setActionLoadingId(exp.id);
      resetMessages();

      const res = await fetchApp<ExpenseWithRecurring>(
        `/expenses/${exp.id}/${action}`,
        {
          method: "PATCH",
        },
      );

      setExpenses((prev) =>
        prev.map((item) => (item.id === exp.id ? res : item)),
      );

      const msg = isPaused
        ? "Recurring expense resumed successfully"
        : "Recurring expense paused successfully";

      showSuccessMsg(msg);
      showToast(msg, "success");
    } catch (err: any) {
      const msg = err?.message || `Failed to ${action} recurring expense`;
      showErrorMsg(msg);
      showToast(msg, "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const askDeleteExpense = (exp: ExpenseWithRecurring) => {
    setPendingDelete(exp);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;

    try {
      setConfirmLoading(true);
      resetMessages();

      await fetchApp(`/expenses/${pendingDelete.id}`, {
        method: "DELETE",
      });

      showSuccessMsg("Expense deleted successfully");
      showToast("Expense deleted successfully", "success");
      await fetchExpenses();
    } catch (err: any) {
      const msg = err?.message || "Failed to delete expense";
      showErrorMsg(msg);
      showToast(msg, "error");
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const closeAlert = () => {
    setError(null);
    setSuccess(null);
  };

  const getExpenseTypeBadge = (exp: ExpenseWithRecurring) => {
    if (exp.is_recurring) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
          <Repeat className="h-3 w-3" />
          Recurring
        </span>
      );
    }

    if (exp.recurring_parent_id) {
      return (
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
          Generated
        </span>
      );
    }

    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
        One-off
      </span>
    );
  };

  const getRecurringStatusBadge = (exp: ExpenseWithRecurring) => {
    if (!exp.is_recurring) return null;

    const status = exp.recurrence_status || "ACTIVE";

    const className =
      status === "PAUSED"
        ? "bg-amber-50 text-amber-700 ring-amber-100"
        : status === "ENDED"
          ? "bg-slate-100 text-slate-600 ring-slate-200"
          : "bg-emerald-50 text-emerald-700 ring-emerald-100";

    return (
      <span
        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${className}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-full flex-1 bg-gradient-to-br from-slate-50 via-white to-slate-100 px-4 py-5 lg:px-8">
      <div className="mx-auto w-full max-w-[1560px] space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-3 flex items-center gap-2 text-xs font-medium text-slate-500">
              <span>Finance Management</span>
              <span className="text-slate-300">/</span>
              <span className="text-slate-900">Expenses</span>
            </div>

            <h1 className="text-2xl font-bold tracking-tight text-slate-950 lg:text-3xl">
              Client Expenses
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Record one-off expenses and recurring expenses such as rent,
              bandwidth, subscriptions, salaries, and utilities.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleGenerateDueExpenses}
              disabled={generatingDue}
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 shadow-sm transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {generatingDue ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {generatingDue ? "Generating…" : "Generate Due"}
            </button>

            <button
              onClick={() => {
                resetMessages();
                fetchExpenses();
              }}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
              {loading ? "Refreshing…" : "Refresh"}
            </button>

            <button
              onClick={() => {
                const el = document.getElementById("expense-form");
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              New Expense
            </button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Entries
                </p>
                <p className="mt-1 text-2xl font-bold text-slate-950">
                  {stats.totalCount}
                </p>
                <p className="text-xs text-slate-500">Total expenses</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                <Repeat className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Recurring
                </p>
                <p className="mt-1 text-2xl font-bold text-slate-950">
                  {stats.recurringCount}
                </p>
                <p className="text-xs text-slate-500">Recurring expenses</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Generated
                </p>
                <p className="mt-1 text-2xl font-bold text-slate-950">
                  {stats.generatedCount}
                </p>
                <p className="text-xs text-slate-500">Generated expenses</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600">
                <Coins className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Actual total
                </p>
                <p className="mt-1 text-2xl font-bold text-slate-950">
                  {stats.totalLabel}
                </p>
                <p className="text-xs text-slate-500">Total amount</p>
              </div>
            </div>
          </div>
        </div>

        {(error || success) && (
          <div
            className={`flex items-start justify-between rounded-2xl border px-4 py-3 text-sm shadow-sm ${
              error
                ? "border-red-200 bg-red-50 text-red-800"
                : "border-emerald-200 bg-emerald-50 text-emerald-800"
            }`}
          >
            <p>{error || success}</p>
            <button
              onClick={closeAlert}
              className="ml-4 rounded-full px-2 py-1 text-xs font-medium hover:bg-black/5"
            >
              Close
            </button>
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-[minmax(0,2.2fr)_minmax(410px,1fr)]">
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-5">
              <div>
                <h2 className="text-base font-bold text-slate-950">
                  Expense History
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  Showing recorded expenses, generated entries, and recurring
                  templates.
                </p>
              </div>
              <p className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                Total {expenses.length} entr
                {expenses.length === 1 ? "y" : "ies"}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-slate-700">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/70 text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-5 py-3">Date</th>
                    <th className="px-3 py-3">Category</th>
                    <th className="px-3 py-3">Description</th>
                    <th className="px-3 py-3">Type</th>
                    <th className="px-3 py-3">Amount</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 text-sm">
                  {paginatedExpenses.map((exp) => (
                    <tr
                      key={exp.id}
                      className="transition hover:bg-slate-50/80"
                    >
                      <td className="whitespace-nowrap px-5 py-4 align-top text-xs text-slate-600">
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-slate-700">
                            {exp.expense_date}
                          </span>
                          {exp.created_by_name && (
                            <span className="text-[11px] text-slate-400">
                              by {exp.created_by_name}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="max-w-[160px] px-3 py-4 align-top text-xs font-medium text-slate-600">
                        <span className="line-clamp-2">
                          {exp.category || "—"}
                        </span>
                      </td>

                      <td className="max-w-[260px] px-3 py-4 align-top text-xs text-slate-500">
                        <span className="line-clamp-2">
                          {exp.description || "—"}
                        </span>
                      </td>

                      <td className="px-3 py-4 align-top">
                        <div className="flex flex-col items-start gap-1.5">
                          {getExpenseTypeBadge(exp)}
                          {getRecurringStatusBadge(exp)}
                          {exp.is_recurring && exp.next_occurrence_date && (
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500 ring-1 ring-slate-200">
                              Next: {exp.next_occurrence_date}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-3 py-4 align-top font-bold text-slate-950">
                        {formatAmount(exp.amount)}{" "}
                        <span className="text-xs font-semibold text-slate-500">
                          {exp.currency}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-right align-top">
                        <div className="flex justify-end gap-2">
                          {exp.is_recurring &&
                            exp.recurrence_status !== "ENDED" && (
                              <button
                                onClick={() => handlePauseResumeRecurring(exp)}
                                disabled={actionLoadingId === exp.id}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                              >
                                {exp.recurrence_status === "PAUSED" ? (
                                  <Play className="h-3.5 w-3.5" />
                                ) : (
                                  <Pause className="h-3.5 w-3.5" />
                                )}
                                {exp.recurrence_status === "PAUSED"
                                  ? "Resume"
                                  : "Pause"}
                              </button>
                            )}

                          <button
                            onClick={() => askDeleteExpense(exp)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {expenses.length === 0 && !loading && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-14 text-center align-middle"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <Coins className="h-6 w-6" />
                          </div>

                          <div>
                            <p className="text-sm font-semibold text-slate-700">
                              No expenses recorded yet
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              One-off and recurring expenses for this client
                              will appear here.
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {loading && (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-8 text-center text-xs text-slate-400"
                      >
                        Loading expenses…
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-slate-500">
                Showing {startEntry} to {endEntry} of {expenses.length} entries
              </p>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {Array.from({ length: totalPages })
                  .slice(0, 5)
                  .map((_, index) => {
                    const pageNumber = index + 1;

                    return (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => setPage(pageNumber)}
                        className={`inline-flex h-9 min-w-9 items-center justify-center rounded-xl px-3 text-sm font-semibold transition ${
                          pageNumber === page
                            ? "bg-slate-950 text-white shadow-sm"
                            : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}

                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </section>

          <section
            id="expense-form"
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm xl:sticky xl:top-5 xl:self-start"
          >
            <div className="mb-5">
              <h2 className="text-base font-bold text-slate-950">
                Record New Expense
              </h2>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Add a normal expense or mark it as recurring for costs that
                repeat automatically.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label
                    htmlFor="expense-amount"
                    className="text-xs font-semibold text-slate-700"
                  >
                    Amount
                  </label>
                  <input
                    id="expense-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g. 1500"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-900/10"
                    value={form.amount}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, amount: e.target.value }))
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="expense-currency"
                    className="text-xs font-semibold text-slate-700"
                  >
                    Currency
                  </label>
                  <select
                    id="expense-currency"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-900/10"
                    value={form.currency}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        currency: e.target.value,
                      }))
                    }
                  >
                    {CURRENCIES.map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label
                    htmlFor="expense-date"
                    className="text-xs font-semibold text-slate-700"
                  >
                    Date
                  </label>
                  <input
                    id="expense-date"
                    type="date"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-900/10"
                    value={form.expense_date}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        expense_date: e.target.value,
                        recurrence_start_date: prev.is_recurring
                          ? e.target.value
                          : prev.recurrence_start_date,
                      }))
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="expense-category"
                    className="text-xs font-semibold text-slate-700"
                  >
                    Category
                  </label>
                  <input
                    id="expense-category"
                    type="text"
                    list="expense-category-options"
                    placeholder="e.g. Bandwidth, Equipment, Rent"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-900/10"
                    value={form.category}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        category: e.target.value,
                      }))
                    }
                  />
                  <datalist id="expense-category-options">
                    {CATEGORY_SUGGESTIONS.map((category) => (
                      <option key={category} value={category} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="expense-description"
                  className="text-xs font-semibold text-slate-700"
                >
                  Description
                </label>
                <textarea
                  id="expense-description"
                  rows={4}
                  placeholder="Optional notes about this expense (e.g. supplier, invoice number)."
                  className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-900/10"
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700 transition hover:border-indigo-200 hover:bg-indigo-50/40">
                <span className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <Repeat className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block font-bold text-slate-800">
                      Make this a recurring expense
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">
                      Use this for monthly rent, bandwidth, subscriptions,
                      salaries, utilities, and other repeated costs.
                    </span>
                  </span>
                </span>

                <span
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
                    form.is_recurring ? "bg-indigo-600" : "bg-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.is_recurring}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        is_recurring: e.target.checked,
                        recurrence_start_date: e.target.checked
                          ? prev.expense_date
                          : prev.recurrence_start_date,
                      }))
                    }
                    className="sr-only"
                  />
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow transition ${
                      form.is_recurring ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </span>
              </label>

              {form.is_recurring && (
                <div className="space-y-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <label
                        htmlFor="recurrence-frequency"
                        className="text-xs font-semibold text-slate-700"
                      >
                        Frequency
                      </label>
                      <select
                        id="recurrence-frequency"
                        className="w-full rounded-xl border border-indigo-100 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-500/20"
                        value={form.recurrence_frequency}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            recurrence_frequency: e.target
                              .value as RecurrenceFrequency,
                          }))
                        }
                      >
                        <option value="DAILY">Daily</option>
                        <option value="WEEKLY">Weekly</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="YEARLY">Yearly</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label
                        htmlFor="recurrence-interval"
                        className="text-xs font-semibold text-slate-700"
                      >
                        Repeat every
                      </label>
                      <input
                        id="recurrence-interval"
                        type="number"
                        min="1"
                        step="1"
                        className="w-full rounded-xl border border-indigo-100 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-500/20"
                        value={form.recurrence_interval}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            recurrence_interval: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <p className="rounded-xl bg-white/70 px-3 py-2 text-[11px] leading-5 text-slate-500">
                    1 means every selected period. 2 means every 2 periods.
                  </p>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <label
                        htmlFor="recurrence-start-date"
                        className="text-xs font-semibold text-slate-700"
                      >
                        Start date
                      </label>
                      <input
                        id="recurrence-start-date"
                        type="date"
                        className="w-full rounded-xl border border-indigo-100 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-500/20"
                        value={form.recurrence_start_date}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            recurrence_start_date: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label
                        htmlFor="recurrence-end-date"
                        className="text-xs font-semibold text-slate-700"
                      >
                        End date optional
                      </label>
                      <input
                        id="recurrence-end-date"
                        type="date"
                        className="w-full rounded-xl border border-indigo-100 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-500/20"
                        value={form.recurrence_end_date}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            recurrence_end_date: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saving
                  ? "Saving…"
                  : form.is_recurring
                    ? "Create Recurring Expense"
                    : "Record Expense"}
              </button>
            </form>
          </section>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        title="Delete expense?"
        description={
          pendingDelete
            ? pendingDelete.is_recurring
              ? `This will remove the recurring expense template of ${formatAmount(
                  pendingDelete.amount,
                )} ${pendingDelete.currency}. Generated expenses already created from it may remain unless your backend deletes them too.`
              : `This will remove the expense of ${formatAmount(
                  pendingDelete.amount,
                )} ${pendingDelete.currency} recorded on ${
                  pendingDelete.expense_date
                }.`
            : ""
        }
        confirmLabel="Delete expense"
        cancelLabel="Cancel"
        variant="danger"
        onCancel={() => {
          if (confirmLoading) return;
          setConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default ExpensesPage;
